import { useCallback, useEffect, useState } from "react";
import { HardDrive, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export function DriveConnect() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/drive/status").then((r) => setS(r.data)).catch(() => setS(null)), []);
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search).get("drive");
    if (q) {
      if (q === "connected") toast.success("Google Drive connected. Marketing files will now save to the ZDS Marketing folder.");
      else toast.error(q === "cancelled" ? "Google Drive connection was cancelled" : "Google Drive connection failed. Check the Client ID, Secret and redirect URI.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);
  if (!s) return null;
  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/drive/connect", { app_url: window.location.origin }); window.location.href = data.authorization_url; }
    catch (e) { err(e); setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/drive/sync"); data.last_error ? toast.error(`Drive: ${data.last_error}`) : toast.success(`${data.uploaded} file(s) saved to Google Drive`); await load(); }
    catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <div data-testid="drive-connect" className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs">
      <HardDrive className="h-4 w-4 text-[#A9792C]" />
      {!s.keys_set ? <span data-testid="drive-status" className="text-zinc-500">Google Drive: add the Google Client ID and Secret to enable</span>
        : s.connected ? (
          <>
            <span data-testid="drive-status" className="text-zinc-700">Google Drive connected{ s.email ? ` · ${s.email}` : ""}{s.pending_files ? ` · ${s.pending_files} file(s) not saved yet` : " · all files saved"}</span>
            {s.folder_link && <a data-testid="drive-open-folder" href={s.folder_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#A9792C] underline"><ExternalLink className="h-3 w-3" />Open folder</a>}
            <Button data-testid="drive-sync" size="sm" variant="outline" className="h-7" disabled={busy} onClick={sync}><RefreshCw className="mr-1 h-3 w-3" />Save files now</Button>
            {s.last_error && <span data-testid="drive-error" className="text-rose-600">Last error: {s.last_error}</span>}
          </>
        ) : (
          <>
            <span data-testid="drive-status" className="text-zinc-500">Google Drive not connected</span>
            <Button data-testid="drive-connect-button" size="sm" className="h-7" disabled={busy} onClick={connect}>Connect Google Drive</Button>
          </>
        )}
    </div>
  );
}
